import React, { type CSSProperties } from 'react';
import {
    ArrowLeft,
    ArrowRight,
    Award,
    BadgeCheck,
    BriefcaseBusiness,
    Building2,
    CalendarDays,
    Camera,
    CheckCircle2,
    CircleDot,
    ExternalLink,
    Facebook,
    Globe2,
    Hammer,
    HeartHandshake,
    IdCard,
    Instagram,
    Linkedin,
    Mail,
    MapPin,
    MessageCircle,
    Phone,
    PlayCircle,
    ShieldCheck,
    Sparkles,
    Star,
    Trophy,
    Users,
    Wrench,
    Youtube,
} from 'lucide-react';
import {
    aboutProfileIconTokens,
    aboutProfileTokens,
    type AboutProfileColorToken,
    type AboutProfileIconToken,
} from './AboutProfile.tokens';

export type AboutProfileLinkComponent = React.ComponentType<{
    href: string;
    children?: React.ReactNode;
    className?: string;
    title?: string;
    target?: string;
    rel?: string;
    'aria-label'?: string;
    [key: string]: any;
}>;

export type AboutProfileIcon = AboutProfileIconToken | React.ReactNode | React.ElementType | false;

export type AboutProfileImageSource =
    | string
    | {
          src?: string;
          width?: number;
          height?: number;
          [key: string]: unknown;
      };

export type AboutProfileImage = {
    id?: string | number;
    src?: AboutProfileImageSource;
    url?: string;
    original?: string;
    thumbnail?: string;
    alt?: string;
    title?: string;
    caption?: React.ReactNode;
    description?: React.ReactNode;
    width?: number;
    height?: number;
    objectPosition?: string;
    loading?: 'eager' | 'lazy';
    decoding?: 'async' | 'auto' | 'sync';
    decorative?: boolean;
};

export type AboutProfileVideo = {
    type?: 'video';
    src: string;
    poster?: string;
    title?: React.ReactNode;
    caption?: React.ReactNode;
    description?: React.ReactNode;
    width?: number;
    height?: number;
    controls?: boolean;
    autoPlay?: boolean;
    muted?: boolean;
    loop?: boolean;
    playsInline?: boolean;
    preload?: 'auto' | 'metadata' | 'none';
};

export type AboutProfileHeroMedia =
    | string
    | AboutProfileImage
    | (AboutProfileImage & { type: 'image' })
    | (AboutProfileVideo & { type: 'video' });

export type AboutProfileAction = {
    label: React.ReactNode;
    href: string;
    ariaLabel?: string;
    title?: string;
    target?: string;
    rel?: string;
    variant?: 'primary' | 'secondary' | 'text';
    icon?: AboutProfileIcon;
    iconSrc?: string;
    iconAlt?: string;
};

export type AboutProfileMetric = {
    id?: string | number;
    value: React.ReactNode;
    label: React.ReactNode;
    description?: React.ReactNode;
    icon?: AboutProfileIcon;
    iconSrc?: string;
    iconAlt?: string;
};

export type AboutProfileFact = {
    id?: string | number;
    label: React.ReactNode;
    value: React.ReactNode;
    description?: React.ReactNode;
    href?: string;
    ariaLabel?: string;
    title?: string;
    target?: string;
    rel?: string;
    icon?: AboutProfileIcon;
    iconSrc?: string;
    iconAlt?: string;
};

export type AboutProfilePoint = {
    id?: string | number;
    title?: React.ReactNode;
    text?: React.ReactNode;
    description?: React.ReactNode;
    icon?: AboutProfileIcon;
    iconSrc?: string;
    iconAlt?: string;
};

export type AboutProfileCoreValue = AboutProfilePoint;

export type AboutProfilePreferredJob = AboutProfilePoint;

export type AboutProfileTool = {
    id?: string | number;
    name: React.ReactNode;
    description?: React.ReactNode;
    icon?: AboutProfileIcon;
    iconSrc?: string;
    iconAlt?: string;
};

export type AboutProfileTeamMember = {
    id?: string | number;
    name: React.ReactNode;
    role?: React.ReactNode;
    description?: React.ReactNode;
    image?: string | AboutProfileImage | false;
    imageAlt?: string;
    links?: AboutProfilePlatformLink[];
};

export type AboutProfileCredential = {
    id?: string | number;
    label: React.ReactNode;
    value?: React.ReactNode;
    issuer?: React.ReactNode;
    description?: React.ReactNode;
    href?: string;
    ariaLabel?: string;
    title?: string;
    target?: string;
    rel?: string;
    icon?: AboutProfileIcon;
    iconSrc?: string;
    iconAlt?: string;
};

export type AboutProfilePlatformLink = {
    id?: string | number;
    label: React.ReactNode;
    href: string;
    description?: React.ReactNode;
    platform?: React.ReactNode;
    ariaLabel?: string;
    title?: string;
    target?: string;
    rel?: string;
    icon?: AboutProfileIcon;
    iconSrc?: string;
    iconAlt?: string;
};

export type AboutProfileTag = {
    id?: string | number;
    label: React.ReactNode;
    href?: string;
    ariaLabel?: string;
    title?: string;
    target?: string;
    rel?: string;
};

export type AboutProfileTextSection = {
    eyebrow?: React.ReactNode;
    title?: React.ReactNode;
    description?: React.ReactNode;
    body?: React.ReactNode;
    paragraphs?: React.ReactNode[];
    icon?: AboutProfileIcon;
    iconSrc?: string;
    iconAlt?: string;
};

export type AboutProfileItemSection<TItem> = AboutProfileTextSection & {
    items?: TItem[];
};

export type AboutProfileGalleryImage = AboutProfileImage & {
    lightboxSrc?: string;
    href?: string;
};

export type AboutProfileGallerySection = AboutProfileTextSection & {
    images?: AboutProfileGalleryImage[];
    lightboxLabel?: string;
    enableLightbox?: boolean;
};

export type AboutProfileMap = {
    title?: React.ReactNode;
    description?: React.ReactNode;
    embedUrl?: string;
    image?: string | AboutProfileImage | false;
    link?: AboutProfileAction | false;
    aspectRatio?: string;
    iframeTitle?: string;
    loading?: 'eager' | 'lazy';
};

export type AboutProfileContactMethod = {
    id?: string | number;
    label: React.ReactNode;
    value: React.ReactNode;
    href?: string;
    description?: React.ReactNode;
    ariaLabel?: string;
    title?: string;
    target?: string;
    rel?: string;
    icon?: AboutProfileIcon;
    iconSrc?: string;
    iconAlt?: string;
};

export type AboutProfileContactSection = AboutProfileTextSection & {
    methods?: AboutProfileContactMethod[];
    action?: AboutProfileAction | false;
};

export type AboutProfileColors = Partial<Record<AboutProfileColorToken, string>>;

export type AboutProfileProps = {
    eyebrow?: React.ReactNode;
    title?: React.ReactNode;
    description?: React.ReactNode;
    hero?: AboutProfileHeroMedia | false;
    heroImage?: string | AboutProfileImage | false;
    heroVideo?: AboutProfileVideo | false;
    heroCaption?: React.ReactNode;
    stats?: AboutProfileMetric[];
    quickFacts?: AboutProfileFact[];
    facts?: AboutProfileFact[];
    journey?: AboutProfileTextSection | false;
    coreValues?: AboutProfileItemSection<AboutProfileCoreValue> | AboutProfileCoreValue[] | false;
    preferredJobs?: AboutProfileItemSection<AboutProfilePreferredJob> | AboutProfilePreferredJob[] | false;
    specialties?: AboutProfileItemSection<AboutProfilePoint> | AboutProfilePoint[] | false;
    difference?: AboutProfileItemSection<AboutProfilePoint> | AboutProfilePoint[] | false;
    team?: AboutProfileItemSection<AboutProfileTeamMember> | AboutProfileTeamMember[] | false;
    teamMembers?: AboutProfileTeamMember[];
    loveOurJob?: AboutProfileTextSection | false;
    tools?: AboutProfileItemSection<AboutProfileTool> | AboutProfileTool[] | false;
    credentials?: AboutProfileItemSection<AboutProfileCredential> | AboutProfileCredential[] | false;
    serviceAreas?: AboutProfileItemSection<AboutProfileTag> | AboutProfileTag[] | false;
    platformLinks?: AboutProfileItemSection<AboutProfilePlatformLink> | AboutProfilePlatformLink[] | false;
    gallery?: AboutProfileGallerySection | AboutProfileGalleryImage[] | false;
    map?: AboutProfileMap | false;
    contact?: AboutProfileContactSection | false;
    actions?: AboutProfileAction[];
    LinkComponent?: AboutProfileLinkComponent;
    Link?: AboutProfileLinkComponent;
    headingLevel?: 'h1' | 'h2' | 'h3' | 'h4';
    sectionHeadingLevel?: 'h2' | 'h3' | 'h4';
    accentColor?: string;
    accentColour?: string;
    accentDarkColor?: string;
    accentDarkColour?: string;
    accentSoftColor?: string;
    accentSoftColour?: string;
    actionColor?: string;
    actionColour?: string;
    actionDarkColor?: string;
    actionDarkColour?: string;
    backgroundColor?: string;
    backgroundColour?: string;
    surfaceColor?: string;
    surfaceColour?: string;
    textColor?: string;
    textColour?: string;
    textSoftColor?: string;
    textSoftColour?: string;
    textMutedColor?: string;
    textMutedColour?: string;
    colors?: AboutProfileColors;
    colours?: AboutProfileColors;
    maxWidth?: number | string;
    id?: string;
    className?: string;
    style?: CSSProperties;
    ariaLabel?: string;
};

type AboutProfileCssVars = CSSProperties &
    Record<`--about-profile-${string}`, string | number | undefined>;

type NormalizedImage = {
    id?: string | number;
    src: string;
    alt: string;
    title?: string;
    caption?: React.ReactNode;
    description?: React.ReactNode;
    width?: number;
    height?: number;
    objectPosition?: string;
    loading: 'eager' | 'lazy';
    decoding: 'async' | 'auto' | 'sync';
    decorative: boolean;
};

const t = aboutProfileTokens;
const iconTokenSet = new Set<string>(aboutProfileIconTokens);

const iconRegistry: Record<AboutProfileIconToken, React.ElementType> = {
    arrowRight: ArrowRight,
    award: Award,
    badgeCheck: BadgeCheck,
    briefcase: BriefcaseBusiness,
    building: Building2,
    calendar: CalendarDays,
    camera: Camera,
    checkCircle: CheckCircle2,
    dot: CircleDot,
    externalLink: ExternalLink,
    facebook: Facebook,
    globe: Globe2,
    hammer: Hammer,
    heartHandshake: HeartHandshake,
    idCard: IdCard,
    instagram: Instagram,
    linkedin: Linkedin,
    mail: Mail,
    mapPin: MapPin,
    messageCircle: MessageCircle,
    phone: Phone,
    play: PlayCircle,
    shieldCheck: ShieldCheck,
    sparkles: Sparkles,
    star: Star,
    trophy: Trophy,
    users: Users,
    wrench: Wrench,
    youtube: Youtube,
};

const aboutProfileCss = `
.about-profile {
  --about-profile-bg: ${t.colors.background};
  --about-profile-surface: ${t.colors.surface};
  --about-profile-surface-soft: ${t.colors.surfaceSoft};
  --about-profile-text: ${t.colors.text};
  --about-profile-text-soft: ${t.colors.textSoft};
  --about-profile-text-muted: ${t.colors.textMuted};
  --about-profile-accent: ${t.colors.accent};
  --about-profile-accent-dark: ${t.colors.accentDark};
  --about-profile-accent-soft: ${t.colors.accentSoft};
  --about-profile-accent-wash: ${t.colors.accentWash};
  --about-profile-accent-contrast: ${t.colors.accentContrast};
  --about-profile-action: ${t.colors.action};
  --about-profile-action-dark: ${t.colors.actionDark};
  --about-profile-action-soft: ${t.colors.actionSoft};
  --about-profile-action-contrast: ${t.colors.actionContrast};
  --about-profile-border: ${t.colors.border};
  --about-profile-border-strong: ${t.colors.borderStrong};
  --about-profile-shadow: ${t.colors.shadow};
  --about-profile-soft-shadow: ${t.colors.softShadow};
  --about-profile-media-fallback: ${t.colors.mediaFallback};
  --about-profile-map-bg: ${t.colors.mapBackground};
  --about-profile-max-width: ${t.sizes.maxWidth}px;
  width: 100%;
  min-width: 0;
  overflow: clip;
  padding: ${t.spacing.sectionY}px ${t.spacing.sectionX}px;
  background:
    linear-gradient(180deg, var(--about-profile-bg) 0%, #ffffff 54%, var(--about-profile-bg) 100%);
  color: var(--about-profile-text);
  font-family: ${t.typography.fontFamily};
  box-sizing: border-box;
  container: about-profile / inline-size;
}
.about-profile,
.about-profile * {
  box-sizing: border-box;
}
.about-profile a {
  color: inherit;
}
.about-profile-shell {
  width: 100%;
  max-width: var(--about-profile-max-width);
  margin: 0 auto;
}
.about-profile-hero {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 420px), 1fr));
  gap: ${t.spacing.heroGap}px;
  align-items: center;
}
.about-profile-hero-content {
  min-width: 0;
}
.about-profile-eyebrow,
.about-profile-section-eyebrow {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  margin: 0 0 14px;
  color: var(--about-profile-accent);
  font-size: ${t.typography.eyebrow.fontSize}px;
  line-height: ${t.typography.eyebrow.lineHeight};
  font-weight: ${t.typography.eyebrow.fontWeight};
  letter-spacing: ${t.typography.eyebrow.letterSpacing};
  text-transform: ${t.typography.eyebrow.textTransform};
  overflow-wrap: break-word;
  word-break: normal;
}
.about-profile-title {
  max-width: 720px;
  margin: 0;
  color: var(--about-profile-text);
  font-size: ${t.typography.title.fontSize}px;
  line-height: ${t.typography.title.lineHeight};
  font-weight: ${t.typography.title.fontWeight};
  letter-spacing: ${t.typography.title.letterSpacing};
  overflow-wrap: break-word;
  word-break: normal;
}
.about-profile-description {
  max-width: 680px;
  margin: 20px 0 0;
  color: var(--about-profile-text-soft);
  font-size: 18px;
  line-height: 1.62;
  font-weight: 520;
  letter-spacing: 0;
  overflow-wrap: break-word;
  word-break: normal;
}
.about-profile-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 28px;
}
.about-profile-action {
  display: inline-flex;
  min-height: 46px;
  max-width: 100%;
  align-items: center;
  justify-content: center;
  gap: 9px;
  border: 1px solid transparent;
  border-radius: ${t.radius.button}px;
  padding: 11px 16px;
  font-size: 14px;
  line-height: 1.2;
  font-weight: 800;
  letter-spacing: 0;
  text-decoration: none;
  transition: background-color 160ms ease, border-color 160ms ease, color 160ms ease, transform 160ms ease;
}
.about-profile-action:hover {
  transform: translateY(-1px);
}
.about-profile-action:focus-visible,
.about-profile-gallery-link:focus-visible,
.about-profile-close:focus-visible,
.about-profile-map-link:focus-visible,
.about-profile-pill:focus-visible,
.about-profile-platform-link:focus-visible,
.about-profile-fact-link:focus-visible,
.about-profile-contact-link:focus-visible,
.about-profile-credential-link:focus-visible,
.about-profile-person-link:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--about-profile-action) 30%, transparent);
  outline-offset: 3px;
}
.about-profile-action--primary {
  background: var(--about-profile-action);
  color: var(--about-profile-action-contrast);
  box-shadow: 0 13px 30px color-mix(in srgb, var(--about-profile-action) 24%, transparent);
}
.about-profile-action--primary:hover {
  background: var(--about-profile-action-dark);
}
.about-profile-action--secondary {
  border-color: var(--about-profile-border-strong);
  background: var(--about-profile-surface);
  color: var(--about-profile-text);
}
.about-profile-action--secondary:hover {
  border-color: var(--about-profile-action);
  color: var(--about-profile-action-dark);
}
.about-profile-action--text {
  min-height: 0;
  padding: 8px 0;
  background: transparent;
  color: var(--about-profile-action-dark);
}
.about-profile-action-icon,
.about-profile-icon,
.about-profile-small-icon,
.about-profile-section-icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  color: currentColor;
}
.about-profile-action-icon {
  width: 18px;
  height: 18px;
}
.about-profile-action-icon svg,
.about-profile-icon svg,
.about-profile-small-icon svg,
.about-profile-section-icon svg {
  display: block;
  width: 100%;
  height: 100%;
  stroke: currentColor;
}
.about-profile-icon img,
.about-profile-small-icon img,
.about-profile-section-icon img,
.about-profile-action-icon img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.about-profile-hero-media {
  min-width: 0;
}
.about-profile-media-frame {
  position: relative;
  min-height: ${t.sizes.heroMediaMinHeight}px;
  margin: 0;
  overflow: hidden;
  border: 1px solid var(--about-profile-border);
  border-radius: ${t.radius.media}px;
  background: var(--about-profile-media-fallback);
  box-shadow: var(--about-profile-shadow);
}
.about-profile-media-image,
.about-profile-media-video {
  display: block;
  width: 100%;
  min-height: ${t.sizes.heroMediaMinHeight}px;
  height: 100%;
  object-fit: cover;
}
.about-profile-media-video {
  background: #111827;
}
.about-profile-media-frame figcaption {
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 16px;
  max-width: calc(100% - 32px);
  width: fit-content;
  border-radius: ${t.radius.tile}px;
  background: rgba(17, 24, 39, 0.78);
  color: #ffffff;
  padding: 8px 10px;
  font-size: 13px;
  line-height: 1.35;
  font-weight: 700;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.about-profile-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin: 30px 0 0;
  padding: 0;
  list-style: none;
}
.about-profile-stat {
  min-width: 0;
  display: grid;
  grid-template-columns: ${t.sizes.smallIcon + 10}px minmax(0, 1fr);
  grid-template-areas:
    "icon value"
    "label label"
    "description description";
  align-items: center;
  column-gap: 14px;
  border: 1px solid var(--about-profile-border);
  border-radius: ${t.radius.tile}px;
  background: var(--about-profile-surface);
  padding: 18px;
}
.about-profile-stat-top {
  display: contents;
}
.about-profile-stat .about-profile-small-icon {
  grid-area: icon;
  width: ${t.sizes.smallIcon}px;
  height: ${t.sizes.smallIcon}px;
  border-radius: ${t.radius.icon}px;
  background: var(--about-profile-accent-soft);
  color: var(--about-profile-accent);
  padding: 7px;
}
.about-profile-stat-value {
  grid-area: value;
  min-width: 0;
  color: var(--about-profile-text);
  font-size: 28px;
  line-height: 1;
  font-weight: 880;
  letter-spacing: 0;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  overflow-wrap: normal;
  word-break: keep-all;
}
.about-profile-stat-label {
  grid-area: label;
  margin: 18px 0 0;
  color: var(--about-profile-text-soft);
  font-size: 13px;
  line-height: 1.35;
  font-weight: 760;
  letter-spacing: 0;
  overflow-wrap: break-word;
  word-break: normal;
}
.about-profile-stat-description {
  grid-area: description;
  margin: 7px 0 0;
  color: var(--about-profile-text-muted);
  font-size: 12px;
  line-height: 1.45;
  font-weight: 520;
  letter-spacing: 0;
}
.about-profile-facts {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: ${t.spacing.tileGap}px;
  margin: 34px 0 0;
}
.about-profile-fact {
  min-width: 0;
  border: 1px solid var(--about-profile-border);
  border-radius: ${t.radius.tile}px;
  background: var(--about-profile-surface);
  padding: 18px;
  box-shadow: var(--about-profile-soft-shadow);
}
.about-profile-fact-heading {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.about-profile-fact .about-profile-small-icon {
  width: ${t.sizes.smallIcon}px;
  height: ${t.sizes.smallIcon}px;
  border-radius: ${t.radius.icon}px;
  background: var(--about-profile-action-soft);
  color: var(--about-profile-action);
  padding: 7px;
}
.about-profile-fact dt {
  min-width: 0;
  color: var(--about-profile-text-muted);
  font-size: 12px;
  line-height: 1.25;
  font-weight: 820;
  letter-spacing: 0;
  text-transform: uppercase;
  overflow-wrap: anywhere;
}
.about-profile-fact dd {
  min-width: 0;
  margin: 12px 0 0;
  color: var(--about-profile-text);
  font-size: 16px;
  line-height: 1.35;
  font-weight: 820;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.about-profile-fact-description {
  margin: 8px 0 0;
  color: var(--about-profile-text-muted);
  font-size: 13px;
  line-height: 1.45;
  font-weight: 520;
  letter-spacing: 0;
}
.about-profile-fact-link,
.about-profile-contact-link,
.about-profile-credential-link {
  text-decoration: none;
}
.about-profile-fact-link:hover,
.about-profile-contact-link:hover,
.about-profile-credential-link:hover {
  color: var(--about-profile-action-dark);
}
.about-profile-main {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(300px, 360px);
  gap: 24px;
  align-items: start;
  margin-top: 24px;
}
.about-profile-primary,
.about-profile-aside {
  display: grid;
  gap: 18px;
  min-width: 0;
}
.about-profile-two-column {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}
.about-profile-panel {
  min-width: 0;
  border: 1px solid var(--about-profile-border);
  border-radius: ${t.radius.panel}px;
  background: var(--about-profile-surface);
  padding: 24px;
  box-shadow: var(--about-profile-soft-shadow);
}
.about-profile-panel--soft {
  background:
    linear-gradient(135deg, var(--about-profile-accent-wash), transparent 42%),
    var(--about-profile-surface);
}
.about-profile-panel--accent {
  border-color: color-mix(in srgb, var(--about-profile-accent) 28%, var(--about-profile-border));
  background:
    linear-gradient(135deg, var(--about-profile-accent-soft), transparent 44%),
    var(--about-profile-surface);
}
.about-profile-section-heading {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 12px;
  align-items: start;
  margin: 0 0 16px;
}
.about-profile-section-heading[data-has-icon="false"] {
  grid-template-columns: minmax(0, 1fr);
}
.about-profile-section-icon {
  width: ${t.sizes.icon}px;
  height: ${t.sizes.icon}px;
  border-radius: ${t.radius.icon}px;
  background: var(--about-profile-accent-soft);
  color: var(--about-profile-accent);
  padding: 9px;
}
.about-profile-section-title {
  margin: 0;
  color: var(--about-profile-text);
  font-size: ${t.typography.sectionTitle.fontSize}px;
  line-height: ${t.typography.sectionTitle.lineHeight};
  font-weight: ${t.typography.sectionTitle.fontWeight};
  letter-spacing: ${t.typography.sectionTitle.letterSpacing};
  overflow-wrap: anywhere;
}
.about-profile-section-description,
.about-profile-section-body,
.about-profile-section-paragraph {
  margin: 10px 0 0;
  color: var(--about-profile-text-soft);
  font-size: ${t.typography.body.fontSize}px;
  line-height: ${t.typography.body.lineHeight};
  font-weight: ${t.typography.body.fontWeight};
  letter-spacing: ${t.typography.body.letterSpacing};
  overflow-wrap: anywhere;
}
.about-profile-section-body > :first-child {
  margin-top: 0;
}
.about-profile-section-body > :last-child {
  margin-bottom: 0;
}
.about-profile-list {
  display: grid;
  gap: 12px;
  margin: 18px 0 0;
  padding: 0;
  list-style: none;
}
.about-profile-point,
.about-profile-tool,
.about-profile-credential {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 11px;
  min-width: 0;
}
.about-profile-point .about-profile-small-icon,
.about-profile-tool .about-profile-small-icon,
.about-profile-credential .about-profile-small-icon,
.about-profile-platform-link .about-profile-small-icon,
.about-profile-contact-method .about-profile-small-icon {
  width: ${t.sizes.smallIcon}px;
  height: ${t.sizes.smallIcon}px;
  margin-top: 1px;
  border-radius: ${t.radius.icon}px;
  background: var(--about-profile-accent-soft);
  color: var(--about-profile-accent);
  padding: 7px;
}
.about-profile-point-title,
.about-profile-tool-name,
.about-profile-credential-label,
.about-profile-platform-label,
.about-profile-contact-label {
  display: block;
  margin: 0;
  color: var(--about-profile-text);
  font-size: ${t.typography.cardTitle.fontSize}px;
  line-height: ${t.typography.cardTitle.lineHeight};
  font-weight: ${t.typography.cardTitle.fontWeight};
  letter-spacing: ${t.typography.cardTitle.letterSpacing};
  overflow-wrap: anywhere;
}
.about-profile-point-text,
.about-profile-tool-description,
.about-profile-credential-description,
.about-profile-platform-description,
.about-profile-contact-description,
.about-profile-credential-meta {
  display: block;
  margin: 5px 0 0;
  color: var(--about-profile-text-muted);
  font-size: 13px;
  line-height: 1.5;
  font-weight: 520;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.about-profile-tools-grid,
.about-profile-credentials-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-top: 18px;
}
.about-profile-team-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-top: 18px;
}
.about-profile-person {
  display: grid;
  grid-template-columns: 82px minmax(0, 1fr);
  gap: 14px;
  min-width: 0;
  align-items: start;
  border: 1px solid var(--about-profile-border);
  border-radius: ${t.radius.tile}px;
  background: color-mix(in srgb, var(--about-profile-surface-soft) 58%, #ffffff);
  padding: 14px;
}
.about-profile-person-image {
  display: block;
  width: 82px;
  height: 82px;
  overflow: hidden;
  border-radius: ${t.radius.media}px;
  background: var(--about-profile-media-fallback);
}
.about-profile-person-image img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.about-profile-person-placeholder {
  display: grid;
  width: 82px;
  height: 82px;
  place-items: center;
  border-radius: ${t.radius.media}px;
  background: var(--about-profile-accent-soft);
  color: var(--about-profile-accent);
}
.about-profile-person-placeholder svg {
  width: 34px;
  height: 34px;
}
.about-profile-person-name {
  margin: 0;
  color: var(--about-profile-text);
  font-size: ${t.typography.cardTitle.fontSize}px;
  line-height: ${t.typography.cardTitle.lineHeight};
  font-weight: ${t.typography.cardTitle.fontWeight};
  letter-spacing: ${t.typography.cardTitle.letterSpacing};
  overflow-wrap: anywhere;
}
.about-profile-person-role,
.about-profile-person-description {
  margin: 5px 0 0;
  color: var(--about-profile-text-muted);
  font-size: 13px;
  line-height: 1.5;
  font-weight: 520;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.about-profile-person-role {
  color: var(--about-profile-accent-dark);
  font-weight: 800;
}
.about-profile-person-links {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}
.about-profile-person-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--about-profile-action-dark);
  font-size: 12px;
  line-height: 1.2;
  font-weight: 820;
  text-decoration: none;
}
.about-profile-tool,
.about-profile-credential {
  border: 1px solid var(--about-profile-border);
  border-radius: ${t.radius.tile}px;
  padding: 14px;
  background: color-mix(in srgb, var(--about-profile-surface-soft) 56%, #ffffff);
}
.about-profile-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
  margin: 16px 0 0;
  padding: 0;
  list-style: none;
}
.about-profile-pill {
  display: inline-flex;
  max-width: 100%;
  align-items: center;
  border: 1px solid var(--about-profile-border-strong);
  border-radius: ${t.radius.pill}px;
  background: var(--about-profile-surface);
  color: var(--about-profile-text-soft);
  padding: 8px 11px;
  font-size: 13px;
  line-height: 1.2;
  font-weight: 760;
  letter-spacing: 0;
  text-decoration: none;
  overflow-wrap: anywhere;
}
.about-profile-pill:hover {
  border-color: var(--about-profile-accent);
  color: var(--about-profile-accent-dark);
}
.about-profile-platforms,
.about-profile-contact-methods {
  display: grid;
  gap: 10px;
  margin: 16px 0 0;
  padding: 0;
  list-style: none;
}
.about-profile-platform-link,
.about-profile-contact-method {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  min-width: 0;
  border: 1px solid var(--about-profile-border);
  border-radius: ${t.radius.tile}px;
  background: var(--about-profile-surface);
  padding: 12px;
  text-decoration: none;
}
.about-profile-contact-method {
  grid-template-columns: auto minmax(0, 1fr);
}
.about-profile-platform-link:hover {
  border-color: var(--about-profile-action);
}
.about-profile-platform-meta {
  min-width: 0;
}
.about-profile-platform-external {
  width: 16px;
  height: 16px;
  color: var(--about-profile-text-muted);
}
.about-profile-contact {
  font-style: normal;
}
.about-profile-contact-action {
  margin-top: 16px;
}
.about-profile-map-frame {
  position: relative;
  width: 100%;
  min-height: 230px;
  margin-top: 16px;
  overflow: hidden;
  border: 1px solid var(--about-profile-border);
  border-radius: ${t.radius.media}px;
  background:
    linear-gradient(90deg, rgba(15, 118, 110, 0.08) 1px, transparent 1px),
    linear-gradient(0deg, rgba(37, 99, 235, 0.06) 1px, transparent 1px),
    var(--about-profile-map-bg);
  background-size: 32px 32px, 32px 32px, auto;
  aspect-ratio: var(--about-profile-map-ratio, 4 / 3);
}
.about-profile-map-frame iframe,
.about-profile-map-frame img {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
  object-fit: cover;
}
.about-profile-map-empty {
  display: grid;
  min-height: 230px;
  place-items: center;
  padding: 24px;
  color: var(--about-profile-text-muted);
  text-align: center;
}
.about-profile-map-empty .about-profile-section-icon {
  margin: 0 auto 12px;
}
.about-profile-map-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  color: var(--about-profile-action-dark);
  font-size: 14px;
  line-height: 1.35;
  font-weight: 820;
  letter-spacing: 0;
  text-decoration: none;
}
.about-profile-gallery-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 18px;
}
.about-profile-gallery-item {
  min-width: 0;
  margin: 0;
}
.about-profile-gallery-link {
  position: relative;
  display: block;
  min-height: 0;
  overflow: hidden;
  border: 1px solid var(--about-profile-border);
  border-radius: ${t.radius.media}px;
  background: var(--about-profile-media-fallback);
  aspect-ratio: 4 / 3;
  text-decoration: none;
}
.about-profile-gallery-link img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 220ms ease;
}
.about-profile-gallery-link:hover img {
  transform: scale(1.035);
}
.about-profile-gallery-expand {
  position: absolute;
  right: 10px;
  bottom: 10px;
  display: inline-flex;
  width: 34px;
  height: 34px;
  align-items: center;
  justify-content: center;
  border-radius: ${t.radius.icon}px;
  background: rgba(17, 24, 39, 0.78);
  color: #ffffff;
}
.about-profile-gallery-caption {
  margin: 9px 0 0;
  color: var(--about-profile-text-muted);
  font-size: 13px;
  line-height: 1.4;
  font-weight: 560;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.about-profile-lightbox {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: grid;
  place-items: center;
  padding: 24px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 160ms ease;
}
.about-profile-lightbox:target {
  opacity: 1;
  pointer-events: auto;
}
.about-profile-lightbox-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(10, 15, 25, 0.86);
}
.about-profile-lightbox-figure {
  position: relative;
  z-index: 1;
  width: min(100%, 1040px);
  max-height: calc(100vh - 48px);
  margin: 0;
  overflow: hidden;
  border-radius: ${t.radius.media}px;
  background: #0f172a;
  box-shadow: 0 22px 80px rgba(0, 0, 0, 0.36);
}
.about-profile-lightbox-figure img {
  display: block;
  width: 100%;
  max-height: calc(100vh - 132px);
  object-fit: contain;
  background: #0f172a;
}
.about-profile-lightbox-figure figcaption {
  margin: 0;
  padding: 14px 16px;
  color: #ffffff;
  font-size: 14px;
  line-height: 1.45;
  font-weight: 620;
  letter-spacing: 0;
}
.about-profile-lightbox-controls {
  position: fixed;
  left: 50%;
  bottom: 18px;
  z-index: 2;
  display: flex;
  max-width: calc(100vw - 112px);
  transform: translateX(-50%);
  gap: 10px;
}
.about-profile-lightbox-control {
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: ${t.radius.button}px;
  background: #ffffff;
  color: #111827;
  padding: 9px 12px;
  font-size: 14px;
  line-height: 1;
  font-weight: 800;
  text-decoration: none;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.22);
}
.about-profile-lightbox-control:hover {
  background: var(--about-profile-action);
  color: var(--about-profile-action-contrast);
}
.about-profile-lightbox-control:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--about-profile-action) 30%, transparent);
  outline-offset: 3px;
}
.about-profile-close {
  position: fixed;
  top: 18px;
  right: 18px;
  z-index: 2;
  display: inline-flex;
  min-width: 44px;
  min-height: 44px;
  align-items: center;
  justify-content: center;
  border-radius: ${t.radius.button}px;
  background: #ffffff;
  color: #111827;
  padding: 9px 12px;
  font-size: 14px;
  line-height: 1;
  font-weight: 850;
  text-decoration: none;
}
@supports not (background: color-mix(in srgb, red 10%, transparent)) {
  .about-profile-action--primary {
    box-shadow: var(--about-profile-soft-shadow);
  }
  .about-profile-tool,
  .about-profile-person,
  .about-profile-credential {
    background: var(--about-profile-surface-soft);
  }
}
@media (max-width: 1040px) {
  .about-profile-hero,
  .about-profile-main {
    grid-template-columns: minmax(0, 1fr);
  }
  .about-profile-title {
    font-size: ${t.typography.title.tabletFontSize}px;
  }
  .about-profile-facts {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 760px) {
  .about-profile {
    padding: 36px 16px;
  }
  .about-profile-title {
    font-size: ${t.typography.title.mobileFontSize}px;
  }
  .about-profile-description {
    font-size: ${t.typography.body.mobileFontSize}px;
  }
  .about-profile-media-frame,
  .about-profile-media-image,
  .about-profile-media-video {
    min-height: ${t.sizes.heroMediaMobileMinHeight}px;
  }
  .about-profile-stats,
  .about-profile-facts,
  .about-profile-two-column,
  .about-profile-tools-grid,
  .about-profile-team-grid,
  .about-profile-credentials-grid,
  .about-profile-gallery-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .about-profile-panel,
  .about-profile-fact {
    padding: 18px;
  }
  .about-profile-section-title {
    font-size: ${t.typography.sectionTitle.mobileFontSize}px;
  }
}
@media (max-width: 460px) {
  .about-profile-actions {
    align-items: stretch;
    flex-direction: column;
  }
  .about-profile-action {
    width: 100%;
  }
  .about-profile-lightbox-controls {
    right: 18px;
    bottom: 18px;
    left: 18px;
    max-width: none;
    transform: none;
  }
  .about-profile-lightbox-control {
    flex: 1;
    padding-inline: 10px;
  }
  .about-profile-platform-link {
    grid-template-columns: auto minmax(0, 1fr);
  }
  .about-profile-person {
    grid-template-columns: 64px minmax(0, 1fr);
  }
  .about-profile-person-image,
  .about-profile-person-placeholder {
    width: 64px;
    height: 64px;
  }
  .about-profile-platform-external {
    display: none;
  }
}
@container about-profile (max-width: 1040px) {
  .about-profile-hero,
  .about-profile-main {
    grid-template-columns: minmax(0, 1fr);
  }
  .about-profile-title {
    font-size: ${t.typography.title.tabletFontSize}px;
  }
  .about-profile-facts {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@container about-profile (max-width: 760px) {
  .about-profile-title {
    font-size: ${t.typography.title.mobileFontSize}px;
  }
  .about-profile-description {
    font-size: ${t.typography.body.mobileFontSize}px;
  }
  .about-profile-media-frame,
  .about-profile-media-image,
  .about-profile-media-video {
    min-height: ${t.sizes.heroMediaMobileMinHeight}px;
  }
  .about-profile-stats,
  .about-profile-facts,
  .about-profile-two-column,
  .about-profile-tools-grid,
  .about-profile-team-grid,
  .about-profile-credentials-grid,
  .about-profile-gallery-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .about-profile-panel,
  .about-profile-fact {
    padding: 18px;
  }
  .about-profile-section-title {
    font-size: ${t.typography.sectionTitle.mobileFontSize}px;
  }
}
@container about-profile (max-width: 460px) {
  .about-profile-actions {
    align-items: stretch;
    flex-direction: column;
  }
  .about-profile-action {
    width: 100%;
  }
  .about-profile-lightbox-controls {
    right: 18px;
    bottom: 18px;
    left: 18px;
    max-width: none;
    transform: none;
  }
  .about-profile-lightbox-control {
    flex: 1;
    padding-inline: 10px;
  }
  .about-profile-platform-link {
    grid-template-columns: auto minmax(0, 1fr);
  }
  .about-profile-person {
    grid-template-columns: 64px minmax(0, 1fr);
  }
  .about-profile-person-image,
  .about-profile-person-placeholder {
    width: 64px;
    height: 64px;
  }
  .about-profile-platform-external {
    display: none;
  }
}
`;

const hasContent = (value: React.ReactNode) =>
    value !== undefined && value !== null && value !== false && value !== '';

const getCssSize = (value?: number | string) => {
    if (typeof value === 'number') return `${value}px`;
    return value;
};

const sanitizeId = (value: string) =>
    value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9_-]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'about-profile';

const getItemKey = (item: { id?: string | number; label?: React.ReactNode; title?: React.ReactNode; name?: React.ReactNode }, index: number) =>
    item.id ?? String(item.label ?? item.title ?? item.name ?? index);

const getRel = (target?: string, rel?: string) => {
    if (rel) return rel;
    return target === '_blank' ? 'noopener noreferrer' : undefined;
};

function getImageSource(image?: string | AboutProfileImage | false): string {
    if (!image) return '';
    if (typeof image === 'string') return image;
    if (typeof image.src === 'string') return image.src;
    if (image.src?.src) return image.src.src;
    return image.url || image.original || image.thumbnail || '';
}

function normalizeImage(
    image?: string | AboutProfileImage | false,
    fallbackAlt = '',
    loading: 'eager' | 'lazy' = 'lazy',
): NormalizedImage | null {
    const src = getImageSource(image);
    if (!src) return null;
    if (!image) return null;

    if (typeof image === 'string') {
        return {
            src,
            alt: fallbackAlt,
            loading,
            decoding: 'async',
            decorative: !fallbackAlt,
        };
    }

    return {
        id: image.id,
        src,
        alt: image.alt || image.title || fallbackAlt,
        title: image.title,
        caption: image.caption,
        description: image.description,
        width: image.width || (typeof image.src === 'object' ? image.src.width : undefined),
        height: image.height || (typeof image.src === 'object' ? image.src.height : undefined),
        objectPosition: image.objectPosition,
        loading: image.loading || loading,
        decoding: image.decoding || 'async',
        decorative: image.decorative ?? !(image.alt || fallbackAlt),
    };
}

function isVideoHero(hero?: AboutProfileHeroMedia | false): hero is AboutProfileVideo & { type: 'video' } {
    return !!hero && typeof hero !== 'string' && 'type' in hero && hero.type === 'video';
}

function resolveItemSection<TItem>(
    value: AboutProfileItemSection<TItem> | TItem[] | false | undefined,
    title?: React.ReactNode,
): (AboutProfileItemSection<TItem> & { items: TItem[] }) | null {
    if (!value) return null;

    if (Array.isArray(value)) {
        return value.length ? { title, items: value } : null;
    }

    const items = value.items || [];
    const hasSectionCopy =
        hasContent(value.eyebrow) ||
        hasContent(value.title) ||
        hasContent(value.description) ||
        hasContent(value.body) ||
        !!value.paragraphs?.some(hasContent);

    if (!items.length && !hasSectionCopy) return null;

    return {
        ...value,
        title: value.title ?? title,
        items,
    };
}

function resolveGallery(
    value: AboutProfileGallerySection | AboutProfileGalleryImage[] | false | undefined,
): (AboutProfileGallerySection & { images: AboutProfileGalleryImage[] }) | null {
    if (!value) return null;
    if (Array.isArray(value)) {
        return value.length ? { title: 'Gallery', images: value, enableLightbox: true } : null;
    }

    const images = value.images || [];
    const hasSectionCopy =
        hasContent(value.eyebrow) ||
        hasContent(value.title) ||
        hasContent(value.description) ||
        hasContent(value.body) ||
        !!value.paragraphs?.some(hasContent);

    if (!images.length && !hasSectionCopy) return null;

    return {
        ...value,
        images,
        enableLightbox: value.enableLightbox ?? true,
    };
}

function renderIcon(
    icon?: AboutProfileIcon,
    className = 'about-profile-small-icon',
    iconSrc?: string,
    iconAlt?: string,
    fallback?: AboutProfileIconToken,
) {
    if (iconSrc) {
        return (
            <span className={className} aria-hidden={iconAlt ? undefined : true}>
                <img src={iconSrc} alt={iconAlt || ''} loading="lazy" />
            </span>
        );
    }

    const resolvedIcon = icon ?? fallback;
    if (!resolvedIcon) return null;

    if (typeof resolvedIcon === 'string') {
        if (!iconTokenSet.has(resolvedIcon)) return null;
        const Icon = iconRegistry[resolvedIcon as AboutProfileIconToken];
        return (
            <span className={className} aria-hidden="true">
                <Icon strokeWidth={2.2} />
            </span>
        );
    }

    if (React.isValidElement(resolvedIcon)) {
        return (
            <span className={className} aria-hidden="true">
                {resolvedIcon}
            </span>
        );
    }

    const Icon = resolvedIcon as React.ElementType;
    return (
        <span className={className} aria-hidden="true">
            <Icon strokeWidth={2.2} />
        </span>
    );
}

function renderAction(
    action: AboutProfileAction,
    LinkRenderer: React.ElementType,
    defaultVariant: AboutProfileAction['variant'] = 'secondary',
) {
    const variant = action.variant || defaultVariant;

    return (
        <LinkRenderer
            href={action.href}
            className={`about-profile-action about-profile-action--${variant}`}
            aria-label={action.ariaLabel}
            title={action.title}
            target={action.target}
            rel={getRel(action.target, action.rel)}
        >
            <span>{action.label}</span>
            {renderIcon(action.icon, 'about-profile-action-icon', action.iconSrc, action.iconAlt, 'arrowRight')}
        </LinkRenderer>
    );
}

function SectionHeader({
    section,
    Heading,
    fallbackIcon,
}: {
    section: AboutProfileTextSection;
    Heading: React.ElementType;
    fallbackIcon?: AboutProfileIconToken;
}) {
    const icon = renderIcon(
        section.icon,
        'about-profile-section-icon',
        section.iconSrc,
        section.iconAlt,
        fallbackIcon,
    );
    const hasHeader =
        !!icon || hasContent(section.eyebrow) || hasContent(section.title) || hasContent(section.description);

    if (!hasHeader) return null;

    return (
        <div className="about-profile-section-heading" data-has-icon={icon ? 'true' : 'false'}>
            {icon}
            <div>
                {hasContent(section.eyebrow) ? (
                    <p className="about-profile-section-eyebrow">{section.eyebrow}</p>
                ) : null}
                {hasContent(section.title) ? (
                    <Heading className="about-profile-section-title">{section.title}</Heading>
                ) : null}
                {hasContent(section.description) ? (
                    <p className="about-profile-section-description">{section.description}</p>
                ) : null}
            </div>
        </div>
    );
}

function SectionCopy({ section }: { section: AboutProfileTextSection }) {
    return (
        <>
            {hasContent(section.body) ? (
                <div className="about-profile-section-body">{section.body}</div>
            ) : null}
            {section.paragraphs?.filter(hasContent).map((paragraph, index) => (
                <p className="about-profile-section-paragraph" key={index}>
                    {paragraph}
                </p>
            ))}
        </>
    );
}

function TextPanel({
    section,
    Heading,
    fallbackIcon,
    className = '',
}: {
    section?: AboutProfileTextSection | false;
    Heading: React.ElementType;
    fallbackIcon?: AboutProfileIconToken;
    className?: string;
}) {
    if (!section) return null;

    const hasSection =
        hasContent(section.eyebrow) ||
        hasContent(section.title) ||
        hasContent(section.description) ||
        hasContent(section.body) ||
        !!section.paragraphs?.some(hasContent);

    if (!hasSection) return null;

    return (
        <article className={`about-profile-panel ${className}`.trim()}>
            <SectionHeader section={section} Heading={Heading} fallbackIcon={fallbackIcon} />
            <SectionCopy section={section} />
        </article>
    );
}

function PointsPanel({
    section,
    Heading,
    fallbackIcon,
    className = '',
}: {
    section: AboutProfileItemSection<AboutProfilePoint> & { items: AboutProfilePoint[] };
    Heading: React.ElementType;
    fallbackIcon?: AboutProfileIconToken;
    className?: string;
}) {
    return (
        <article className={`about-profile-panel ${className}`.trim()}>
            <SectionHeader section={section} Heading={Heading} fallbackIcon={fallbackIcon} />
            <SectionCopy section={section} />
            {section.items.length ? (
                <ul className="about-profile-list">
                    {section.items.map((item, index) => (
                        <li className="about-profile-point" key={getItemKey(item, index)}>
                            {renderIcon(item.icon, 'about-profile-small-icon', item.iconSrc, item.iconAlt, 'checkCircle')}
                            <div>
                                {hasContent(item.title) ? (
                                    <p className="about-profile-point-title">{item.title}</p>
                                ) : null}
                                {hasContent(item.text ?? item.description) ? (
                                    <p className="about-profile-point-text">{item.text ?? item.description}</p>
                                ) : null}
                            </div>
                        </li>
                    ))}
                </ul>
            ) : null}
        </article>
    );
}

function ToolsPanel({
    section,
    Heading,
}: {
    section: AboutProfileItemSection<AboutProfileTool> & { items: AboutProfileTool[] };
    Heading: React.ElementType;
}) {
    return (
        <article className="about-profile-panel">
            <SectionHeader section={section} Heading={Heading} fallbackIcon="wrench" />
            <SectionCopy section={section} />
            {section.items.length ? (
                <ul className="about-profile-tools-grid">
                    {section.items.map((item, index) => (
                        <li className="about-profile-tool" key={getItemKey({ ...item, label: item.name }, index)}>
                            {renderIcon(item.icon, 'about-profile-small-icon', item.iconSrc, item.iconAlt, 'wrench')}
                            <div>
                                <p className="about-profile-tool-name">{item.name}</p>
                                {hasContent(item.description) ? (
                                    <p className="about-profile-tool-description">{item.description}</p>
                                ) : null}
                            </div>
                        </li>
                    ))}
                </ul>
            ) : null}
        </article>
    );
}

function TeamPanel({
    section,
    Heading,
    LinkRenderer,
}: {
    section: AboutProfileItemSection<AboutProfileTeamMember> & { items: AboutProfileTeamMember[] };
    Heading: React.ElementType;
    LinkRenderer: React.ElementType;
}) {
    return (
        <article className="about-profile-panel">
            <SectionHeader section={section} Heading={Heading} fallbackIcon="users" />
            <SectionCopy section={section} />
            {section.items.length ? (
                <ul className="about-profile-team-grid">
                    {section.items.map((member, index) => {
                        const image = normalizeImage(member.image, member.imageAlt || String(member.name || ''), 'lazy');

                        return (
                            <li className="about-profile-person" key={getItemKey({ ...member, label: member.name }, index)}>
                                {image ? (
                                    <span className="about-profile-person-image">
                                        <img
                                            src={image.src}
                                            alt={image.decorative ? '' : image.alt}
                                            title={image.title}
                                            width={image.width}
                                            height={image.height}
                                            loading={image.loading}
                                            decoding={image.decoding}
                                            style={image.objectPosition ? { objectPosition: image.objectPosition } : undefined}
                                        />
                                    </span>
                                ) : (
                                    <span className="about-profile-person-placeholder" aria-hidden="true">
                                        <Users />
                                    </span>
                                )}
                                <div>
                                    <p className="about-profile-person-name">{member.name}</p>
                                    {hasContent(member.role) ? (
                                        <p className="about-profile-person-role">{member.role}</p>
                                    ) : null}
                                    {hasContent(member.description) ? (
                                        <p className="about-profile-person-description">{member.description}</p>
                                    ) : null}
                                    {member.links?.length ? (
                                        <div className="about-profile-person-links">
                                            {member.links.map((link, linkIndex) => (
                                                <LinkRenderer
                                                    href={link.href}
                                                    className="about-profile-person-link"
                                                    aria-label={link.ariaLabel}
                                                    title={link.title}
                                                    target={link.target}
                                                    rel={getRel(link.target, link.rel)}
                                                    key={String(link.id || link.href || linkIndex)}
                                                >
                                                    {renderIcon(
                                                        link.icon,
                                                        'about-profile-action-icon',
                                                        link.iconSrc,
                                                        link.iconAlt,
                                                        'externalLink',
                                                    )}
                                                    <span>{link.label}</span>
                                                </LinkRenderer>
                                            ))}
                                        </div>
                                    ) : null}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            ) : null}
        </article>
    );
}

function CredentialsPanel({
    section,
    Heading,
    LinkRenderer,
}: {
    section: AboutProfileItemSection<AboutProfileCredential> & { items: AboutProfileCredential[] };
    Heading: React.ElementType;
    LinkRenderer: React.ElementType;
}) {
    return (
        <article className="about-profile-panel">
            <SectionHeader section={section} Heading={Heading} fallbackIcon="shieldCheck" />
            <SectionCopy section={section} />
            {section.items.length ? (
                <ul className="about-profile-credentials-grid">
                    {section.items.map((item, index) => {
                        const content = (
                            <>
                                {renderIcon(item.icon, 'about-profile-small-icon', item.iconSrc, item.iconAlt, 'badgeCheck')}
                                <div>
                                    <p className="about-profile-credential-label">{item.label}</p>
                                    {hasContent(item.value) ? (
                                        <p className="about-profile-credential-meta">{item.value}</p>
                                    ) : null}
                                    {hasContent(item.issuer) ? (
                                        <p className="about-profile-credential-meta">{item.issuer}</p>
                                    ) : null}
                                    {hasContent(item.description) ? (
                                        <p className="about-profile-credential-description">{item.description}</p>
                                    ) : null}
                                </div>
                            </>
                        );

                        return (
                            <li key={getItemKey(item, index)}>
                                {item.href ? (
                                    <LinkRenderer
                                        href={item.href}
                                        className="about-profile-credential about-profile-credential-link"
                                        aria-label={item.ariaLabel}
                                        title={item.title}
                                        target={item.target}
                                        rel={getRel(item.target, item.rel)}
                                    >
                                        {content}
                                    </LinkRenderer>
                                ) : (
                                    <div className="about-profile-credential">{content}</div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            ) : null}
        </article>
    );
}

function TagsPanel({
    section,
    Heading,
    LinkRenderer,
}: {
    section: AboutProfileItemSection<AboutProfileTag> & { items: AboutProfileTag[] };
    Heading: React.ElementType;
    LinkRenderer: React.ElementType;
}) {
    return (
        <article className="about-profile-panel about-profile-panel--soft">
            <SectionHeader section={section} Heading={Heading} fallbackIcon="mapPin" />
            <SectionCopy section={section} />
            {section.items.length ? (
                <ul className="about-profile-pills">
                    {section.items.map((item, index) => (
                        <li key={getItemKey(item, index)}>
                            {item.href ? (
                                <LinkRenderer
                                    href={item.href}
                                    className="about-profile-pill"
                                    aria-label={item.ariaLabel}
                                    title={item.title}
                                    target={item.target}
                                    rel={getRel(item.target, item.rel)}
                                >
                                    {item.label}
                                </LinkRenderer>
                            ) : (
                                <span className="about-profile-pill">{item.label}</span>
                            )}
                        </li>
                    ))}
                </ul>
            ) : null}
        </article>
    );
}

function PlatformsPanel({
    section,
    Heading,
    LinkRenderer,
}: {
    section: AboutProfileItemSection<AboutProfilePlatformLink> & { items: AboutProfilePlatformLink[] };
    Heading: React.ElementType;
    LinkRenderer: React.ElementType;
}) {
    return (
        <article className="about-profile-panel">
            <SectionHeader section={section} Heading={Heading} fallbackIcon="globe" />
            <SectionCopy section={section} />
            {section.items.length ? (
                <ul className="about-profile-platforms">
                    {section.items.map((item, index) => (
                        <li key={getItemKey(item, index)}>
                            <LinkRenderer
                                href={item.href}
                                className="about-profile-platform-link"
                                aria-label={item.ariaLabel}
                                title={item.title}
                                target={item.target}
                                rel={getRel(item.target, item.rel)}
                            >
                                {renderIcon(item.icon, 'about-profile-small-icon', item.iconSrc, item.iconAlt, 'externalLink')}
                                <span className="about-profile-platform-meta">
                                    <span className="about-profile-platform-label">{item.label}</span>
                                    {hasContent(item.platform) ? (
                                        <span className="about-profile-platform-description">{item.platform}</span>
                                    ) : null}
                                    {hasContent(item.description) ? (
                                        <span className="about-profile-platform-description">{item.description}</span>
                                    ) : null}
                                </span>
                                <ExternalLink className="about-profile-platform-external" aria-hidden="true" />
                            </LinkRenderer>
                        </li>
                    ))}
                </ul>
            ) : null}
        </article>
    );
}

function GalleryPanel({
    section,
    Heading,
    sectionId,
}: {
    section: AboutProfileGallerySection & { images: AboutProfileGalleryImage[] };
    Heading: React.ElementType;
    sectionId: string;
}) {
    const images = section.images
        .map((image, index) => ({
            source: normalizeImage(image, `Gallery image ${index + 1}`),
            lightboxSrc: image.lightboxSrc || image.original || image.url || getImageSource(image),
            href: image.href,
            raw: image,
        }))
        .filter((item) => item.source);

    if (!images.length && !section.title && !section.description) return null;

    const lightboxIds = images.map((item, index) => {
        const image = item.source as NormalizedImage;

        return `${sectionId}-gallery-${sanitizeId(String(image.id ?? index))}`;
    });
    const hasLightboxNavigation = section.enableLightbox !== false && images.length > 1;

    return (
        <article className="about-profile-panel">
            <SectionHeader section={section} Heading={Heading} fallbackIcon="camera" />
            <SectionCopy section={section} />
            {images.length ? (
                <div className="about-profile-gallery-grid">
                    {images.map((item, index) => {
                        const image = item.source as NormalizedImage;
                        const lightboxId = lightboxIds[index];
                        const previousLightboxId = lightboxIds[(index - 1 + lightboxIds.length) % lightboxIds.length];
                        const nextLightboxId = lightboxIds[(index + 1) % lightboxIds.length];
                        const caption = image.caption || image.description || image.title || image.alt;
                        const href =
                            section.enableLightbox === false
                                ? item.href || item.lightboxSrc || image.src
                                : `#${lightboxId}`;

                        return (
                            <figure className="about-profile-gallery-item" key={getItemKey(image, index)}>
                                <a
                                    className="about-profile-gallery-link"
                                    href={href}
                                    aria-label={`Open ${image.alt || 'gallery image'}`}
                                >
                                    <img
                                        src={image.src}
                                        alt={image.decorative ? '' : image.alt}
                                        title={image.title}
                                        width={image.width}
                                        height={image.height}
                                        loading={image.loading}
                                        decoding={image.decoding}
                                        style={image.objectPosition ? { objectPosition: image.objectPosition } : undefined}
                                    />
                                    <span className="about-profile-gallery-expand" aria-hidden="true">
                                        <Camera size={18} strokeWidth={2.2} />
                                    </span>
                                </a>
                                {hasContent(caption) ? (
                                    <figcaption className="about-profile-gallery-caption">{caption}</figcaption>
                                ) : null}
                                {section.enableLightbox !== false ? (
                                    <div
                                        id={lightboxId}
                                        className="about-profile-lightbox"
                                        role="dialog"
                                        aria-modal="true"
                                        aria-label={section.lightboxLabel || 'Image preview'}
                                        tabIndex={-1}
                                    >
                                        <a
                                            className="about-profile-lightbox-backdrop"
                                            href={`#${sectionId}`}
                                            aria-label="Close image preview"
                                        />
                                        <figure className="about-profile-lightbox-figure">
                                            <img
                                                src={item.lightboxSrc || image.src}
                                                alt={image.decorative ? '' : image.alt}
                                            />
                                            {hasContent(caption) ? <figcaption>{caption}</figcaption> : null}
                                        </figure>
                                        {hasLightboxNavigation ? (
                                            <nav
                                                className="about-profile-lightbox-controls"
                                                aria-label="Gallery image navigation"
                                            >
                                                <a
                                                    className="about-profile-lightbox-control"
                                                    href={`#${previousLightboxId}`}
                                                    aria-label="View previous gallery image"
                                                >
                                                    <ArrowLeft size={18} strokeWidth={2.4} aria-hidden="true" />
                                                    <span>Previous</span>
                                                </a>
                                                <a
                                                    className="about-profile-lightbox-control"
                                                    href={`#${nextLightboxId}`}
                                                    aria-label="View next gallery image"
                                                >
                                                    <span>Next</span>
                                                    <ArrowRight size={18} strokeWidth={2.4} aria-hidden="true" />
                                                </a>
                                            </nav>
                                        ) : null}
                                        <a className="about-profile-close" href={`#${sectionId}`}>
                                            Close
                                        </a>
                                    </div>
                                ) : null}
                            </figure>
                        );
                    })}
                </div>
            ) : null}
        </article>
    );
}

function MapPanel({
    map,
    Heading,
    LinkRenderer,
}: {
    map?: AboutProfileMap | false;
    Heading: React.ElementType;
    LinkRenderer: React.ElementType;
}) {
    if (!map) return null;

    const image = normalizeImage(map.image, '', 'lazy');
    const section: AboutProfileTextSection = {
        title: map.title,
        description: map.description,
        icon: 'mapPin',
    };
    const hasMapContent = hasContent(map.title) || hasContent(map.description) || map.embedUrl || image || map.link;

    if (!hasMapContent) return null;

    return (
        <article className="about-profile-panel">
            <SectionHeader section={section} Heading={Heading} fallbackIcon="mapPin" />
            <div
                className="about-profile-map-frame"
                style={map.aspectRatio ? ({ '--about-profile-map-ratio': map.aspectRatio } as CSSProperties) : undefined}
            >
                {map.embedUrl ? (
                    <iframe
                        src={map.embedUrl}
                        title={map.iframeTitle || 'Map location'}
                        loading={map.loading || 'lazy'}
                        allowFullScreen
                        referrerPolicy="no-referrer-when-downgrade"
                    />
                ) : image ? (
                    <img
                        src={image.src}
                        alt={image.decorative ? '' : image.alt}
                        title={image.title}
                        width={image.width}
                        height={image.height}
                        loading={image.loading}
                        decoding={image.decoding}
                        style={image.objectPosition ? { objectPosition: image.objectPosition } : undefined}
                    />
                ) : (
                    <div className="about-profile-map-empty">
                        {renderIcon('mapPin', 'about-profile-section-icon')}
                        <span>{map.title || 'Service area map'}</span>
                    </div>
                )}
            </div>
            {map.link ? (
                <LinkRenderer
                    href={map.link.href}
                    className="about-profile-map-link"
                    aria-label={map.link.ariaLabel}
                    title={map.link.title}
                    target={map.link.target}
                    rel={getRel(map.link.target, map.link.rel)}
                >
                    <span>{map.link.label}</span>
                    {renderIcon(map.link.icon, 'about-profile-action-icon', map.link.iconSrc, map.link.iconAlt, 'arrowRight')}
                </LinkRenderer>
            ) : null}
        </article>
    );
}

function ContactPanel({
    contact,
    Heading,
    LinkRenderer,
}: {
    contact?: AboutProfileContactSection | false;
    Heading: React.ElementType;
    LinkRenderer: React.ElementType;
}) {
    if (!contact) return null;

    const methods = contact.methods || [];
    const hasContact =
        hasContent(contact.title) ||
        hasContent(contact.description) ||
        hasContent(contact.body) ||
        !!contact.paragraphs?.some(hasContent) ||
        methods.length ||
        contact.action;

    if (!hasContact) return null;

    return (
        <article className="about-profile-panel about-profile-panel--accent">
            <SectionHeader section={contact} Heading={Heading} fallbackIcon="messageCircle" />
            <SectionCopy section={contact} />
            {methods.length ? (
                <address className="about-profile-contact">
                    <ul className="about-profile-contact-methods">
                        {methods.map((method, index) => {
                            const content = (
                                <>
                                    {renderIcon(method.icon, 'about-profile-small-icon', method.iconSrc, method.iconAlt, 'phone')}
                                    <span>
                                        <span className="about-profile-contact-label">{method.label}</span>
                                        <span className="about-profile-platform-description">{method.value}</span>
                                        {hasContent(method.description) ? (
                                            <span className="about-profile-contact-description">{method.description}</span>
                                        ) : null}
                                    </span>
                                </>
                            );

                            return (
                                <li key={getItemKey(method, index)}>
                                    {method.href ? (
                                        <LinkRenderer
                                            href={method.href}
                                            className="about-profile-contact-method about-profile-contact-link"
                                            aria-label={method.ariaLabel}
                                            title={method.title}
                                            target={method.target}
                                            rel={getRel(method.target, method.rel)}
                                        >
                                            {content}
                                        </LinkRenderer>
                                    ) : (
                                        <span className="about-profile-contact-method">{content}</span>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </address>
            ) : null}
            {contact.action ? (
                <div className="about-profile-contact-action">
                    {renderAction(contact.action, LinkRenderer, contact.action.variant || 'primary')}
                </div>
            ) : null}
        </article>
    );
}

function HeroMedia({
    hero,
    heroImage,
    heroVideo,
    caption,
}: {
    hero?: AboutProfileHeroMedia | false;
    heroImage?: string | AboutProfileImage | false;
    heroVideo?: AboutProfileVideo | false;
    caption?: React.ReactNode;
}) {
    const resolvedHero =
        hero === false
            ? false
            : hero ||
              (heroVideo ? ({ ...heroVideo, type: 'video' } as AboutProfileVideo & { type: 'video' }) : undefined) ||
              heroImage ||
              false;

    if (!resolvedHero) return null;

    if (isVideoHero(resolvedHero)) {
        const videoCaption = caption || resolvedHero.caption || resolvedHero.description || resolvedHero.title;

        return (
            <div className="about-profile-hero-media">
                <figure className="about-profile-media-frame">
                    <video
                        className="about-profile-media-video"
                        src={resolvedHero.src}
                        poster={resolvedHero.poster}
                        controls={resolvedHero.controls ?? true}
                        autoPlay={resolvedHero.autoPlay}
                        muted={resolvedHero.muted}
                        loop={resolvedHero.loop}
                        playsInline={resolvedHero.playsInline ?? true}
                        preload={resolvedHero.preload || 'metadata'}
                        width={resolvedHero.width}
                        height={resolvedHero.height}
                    />
                    {hasContent(videoCaption) ? <figcaption>{videoCaption}</figcaption> : null}
                </figure>
            </div>
        );
    }

    const image =
        typeof resolvedHero === 'string' || !('type' in resolvedHero)
            ? normalizeImage(resolvedHero, '', 'eager')
            : normalizeImage(resolvedHero, '', 'eager');

    if (!image) return null;

    const imageCaption = caption || image.caption || image.description || image.title;

    return (
        <div className="about-profile-hero-media">
            <figure className="about-profile-media-frame">
                <img
                    className="about-profile-media-image"
                    src={image.src}
                    alt={image.decorative ? '' : image.alt}
                    title={image.title}
                    width={image.width}
                    height={image.height}
                    loading={image.loading}
                    decoding={image.decoding}
                    style={image.objectPosition ? { objectPosition: image.objectPosition } : undefined}
                />
                {hasContent(imageCaption) ? <figcaption>{imageCaption}</figcaption> : null}
            </figure>
        </div>
    );
}

export default function AboutProfile({
    eyebrow,
    title,
    description,
    hero,
    heroImage,
    heroVideo,
    heroCaption,
    stats = [],
    quickFacts,
    facts,
    journey,
    coreValues,
    preferredJobs,
    specialties,
    difference,
    team,
    teamMembers,
    loveOurJob,
    tools,
    credentials,
    serviceAreas,
    platformLinks,
    gallery,
    map,
    contact,
    actions = [],
    LinkComponent,
    Link,
    headingLevel = 'h1',
    sectionHeadingLevel = 'h2',
    accentColor,
    accentColour,
    accentDarkColor,
    accentDarkColour,
    accentSoftColor,
    accentSoftColour,
    actionColor,
    actionColour,
    actionDarkColor,
    actionDarkColour,
    backgroundColor,
    backgroundColour,
    surfaceColor,
    surfaceColour,
    textColor,
    textColour,
    textSoftColor,
    textSoftColour,
    textMutedColor,
    textMutedColour,
    colors,
    colours,
    maxWidth,
    id,
    className = '',
    style,
    ariaLabel,
}: AboutProfileProps) {
    const LinkRenderer = (LinkComponent || Link || 'a') as React.ElementType;
    const Heading = headingLevel as React.ElementType;
    const SectionHeading = sectionHeadingLevel as React.ElementType;
    const sectionId = sanitizeId(id || 'about-profile');
    const resolvedColors = { ...colours, ...colors };
    const resolvedFacts = facts || quickFacts || [];
    const coreValuesSection = resolveItemSection(coreValues, 'Core values');
    const preferredJobsSection = resolveItemSection(preferredJobs, 'Preferred jobs');
    const specialtiesSection = resolveItemSection(specialties, 'Specialties');
    const differenceSection = resolveItemSection(difference, 'What makes us different');
    const teamSection = resolveItemSection(
        team === undefined ? teamMembers : team,
        "Who you'll deal with",
    );
    const toolsSection = resolveItemSection(tools, 'Tools we use');
    const credentialsSection = resolveItemSection(credentials, 'Licences and certifications');
    const serviceAreasSection = resolveItemSection(serviceAreas, "Where you'll find us");
    const platformLinksSection = resolveItemSection(platformLinks, "Where you'll find us");
    const gallerySection = resolveGallery(gallery);

    const sectionStyle: AboutProfileCssVars = {
        '--about-profile-bg': backgroundColor || backgroundColour || resolvedColors.background,
        '--about-profile-surface': surfaceColor || surfaceColour || resolvedColors.surface,
        '--about-profile-text': textColor || textColour || resolvedColors.text,
        '--about-profile-text-soft': textSoftColor || textSoftColour || resolvedColors.textSoft,
        '--about-profile-text-muted': textMutedColor || textMutedColour || resolvedColors.textMuted,
        '--about-profile-accent': accentColor || accentColour || resolvedColors.accent,
        '--about-profile-accent-dark': accentDarkColor || accentDarkColour || resolvedColors.accentDark,
        '--about-profile-accent-soft': accentSoftColor || accentSoftColour || resolvedColors.accentSoft,
        '--about-profile-action': actionColor || actionColour || resolvedColors.action,
        '--about-profile-action-dark': actionDarkColor || actionDarkColour || resolvedColors.actionDark,
        '--about-profile-max-width': getCssSize(maxWidth),
        ...style,
    };

    const hasHeroText = hasContent(eyebrow) || hasContent(title) || hasContent(description) || actions.length;
    const hasHeroMedia = hero !== false && (hero || heroImage || heroVideo);
    const hasAnyContent =
        hasHeroText ||
        hasHeroMedia ||
        stats.length ||
        resolvedFacts.length ||
        journey ||
        coreValuesSection ||
        preferredJobsSection ||
        specialtiesSection ||
        differenceSection ||
        teamSection ||
        loveOurJob ||
        toolsSection ||
        credentialsSection ||
        serviceAreasSection ||
        platformLinksSection ||
        gallerySection ||
        map ||
        contact;

    if (!hasAnyContent) return null;

    return (
        <section
            id={sectionId}
            className={`about-profile ${className}`.trim()}
            data-component="about-profile"
            aria-label={ariaLabel}
            style={sectionStyle}
        >
            <style>{aboutProfileCss}</style>
            <div className="about-profile-shell">
                {(hasHeroText || hasHeroMedia) ? (
                    <div className="about-profile-hero">
                        {hasHeroText ? (
                            <header className="about-profile-hero-content">
                                {hasContent(eyebrow) ? <p className="about-profile-eyebrow">{eyebrow}</p> : null}
                                {hasContent(title) ? <Heading className="about-profile-title">{title}</Heading> : null}
                                {hasContent(description) ? (
                                    <p className="about-profile-description">{description}</p>
                                ) : null}
                                {actions.length ? (
                                    <div className="about-profile-actions">
                                        {actions.map((action, index) => (
                                            <React.Fragment key={String(action.href || index)}>
                                                {renderAction(
                                                    action,
                                                    LinkRenderer,
                                                    index === 0 ? 'primary' : 'secondary',
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                ) : null}
                                {stats.length ? (
                                    <ul className="about-profile-stats">
                                        {stats.map((stat, index) => (
                                            <li className="about-profile-stat" key={getItemKey(stat, index)}>
                                                <div className="about-profile-stat-top">
                                                    {renderIcon(
                                                        stat.icon,
                                                        'about-profile-small-icon',
                                                        stat.iconSrc,
                                                        stat.iconAlt,
                                                        'star',
                                                    )}
                                                    <div className="about-profile-stat-value">{stat.value}</div>
                                                </div>
                                                <p className="about-profile-stat-label">{stat.label}</p>
                                                {hasContent(stat.description) ? (
                                                    <p className="about-profile-stat-description">{stat.description}</p>
                                                ) : null}
                                            </li>
                                        ))}
                                    </ul>
                                ) : null}
                            </header>
                        ) : null}
                        {hasHeroMedia ? (
                            <HeroMedia
                                hero={hero}
                                heroImage={heroImage}
                                heroVideo={heroVideo}
                                caption={heroCaption}
                            />
                        ) : null}
                    </div>
                ) : null}

                {resolvedFacts.length ? (
                    <dl className="about-profile-facts">
                        {resolvedFacts.map((fact, index) => {
                            const factValue = fact.href ? (
                                <LinkRenderer
                                    href={fact.href}
                                    className="about-profile-fact-link"
                                    aria-label={fact.ariaLabel}
                                    title={fact.title}
                                    target={fact.target}
                                    rel={getRel(fact.target, fact.rel)}
                                >
                                    {fact.value}
                                </LinkRenderer>
                            ) : (
                                fact.value
                            );

                            return (
                                <div className="about-profile-fact" key={getItemKey(fact, index)}>
                                    <div className="about-profile-fact-heading">
                                        {renderIcon(
                                            fact.icon,
                                            'about-profile-small-icon',
                                            fact.iconSrc,
                                            fact.iconAlt,
                                            'idCard',
                                        )}
                                        <dt>{fact.label}</dt>
                                    </div>
                                    <dd>{factValue}</dd>
                                    {hasContent(fact.description) ? (
                                        <p className="about-profile-fact-description">{fact.description}</p>
                                    ) : null}
                                </div>
                            );
                        })}
                    </dl>
                ) : null}

                <div className="about-profile-main">
                    <div className="about-profile-primary">
                        <TextPanel
                            section={journey}
                            Heading={SectionHeading}
                            fallbackIcon="calendar"
                            className="about-profile-panel--soft"
                        />

                        {teamSection ? (
                            <TeamPanel
                                section={teamSection}
                                Heading={SectionHeading}
                                LinkRenderer={LinkRenderer}
                            />
                        ) : null}

                        {coreValuesSection ? (
                            <PointsPanel
                                section={coreValuesSection}
                                Heading={SectionHeading}
                                fallbackIcon="heartHandshake"
                                className="about-profile-panel--accent"
                            />
                        ) : null}

                        {preferredJobsSection ? (
                            <PointsPanel
                                section={preferredJobsSection}
                                Heading={SectionHeading}
                                fallbackIcon="briefcase"
                            />
                        ) : null}

                        {specialtiesSection || differenceSection ? (
                            <div className="about-profile-two-column">
                                {specialtiesSection ? (
                                    <PointsPanel
                                        section={specialtiesSection}
                                        Heading={SectionHeading}
                                        fallbackIcon="sparkles"
                                    />
                                ) : null}
                                {differenceSection ? (
                                    <PointsPanel
                                        section={differenceSection}
                                        Heading={SectionHeading}
                                        fallbackIcon="shieldCheck"
                                    />
                                ) : null}
                            </div>
                        ) : null}

                        <TextPanel
                            section={loveOurJob}
                            Heading={SectionHeading}
                            fallbackIcon="heartHandshake"
                        />

                        {gallerySection ? (
                            <GalleryPanel section={gallerySection} Heading={SectionHeading} sectionId={sectionId} />
                        ) : null}

                        {toolsSection ? <ToolsPanel section={toolsSection} Heading={SectionHeading} /> : null}
                    </div>

                    <aside className="about-profile-aside">
                        <ContactPanel contact={contact} Heading={SectionHeading} LinkRenderer={LinkRenderer} />
                        {serviceAreasSection ? (
                            <TagsPanel
                                section={serviceAreasSection}
                                Heading={SectionHeading}
                                LinkRenderer={LinkRenderer}
                            />
                        ) : null}
                        {platformLinksSection ? (
                            <PlatformsPanel
                                section={platformLinksSection}
                                Heading={SectionHeading}
                                LinkRenderer={LinkRenderer}
                            />
                        ) : null}
                        {credentialsSection ? (
                            <CredentialsPanel
                                section={credentialsSection}
                                Heading={SectionHeading}
                                LinkRenderer={LinkRenderer}
                            />
                        ) : null}
                        <MapPanel map={map} Heading={SectionHeading} LinkRenderer={LinkRenderer} />
                    </aside>
                </div>
            </div>
        </section>
    );
}

export { aboutProfileIconTokens, aboutProfileTokens };
export type { AboutProfileColorToken, AboutProfileIconToken };
